import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { errorDetail } from '../integrations/mcpServerRegistry';
import ServiceOrders from './ServiceOrders';

/** Mirrors ServicePriceService.serialize (backend). */
export interface ServicePrice {
    id: string;
    service_id?: string;
    status: 'draft' | 'active' | 'archived';
    display_name: string | null;
    label: string;
    billing_type: 'one_time' | 'subscription';
    currency: string;
    unit_amount_minor: number;
    recurring_interval: 'day' | 'week' | 'month' | 'year' | null;
    recurring_interval_count: number | null;
    access_duration_type: 'perpetual' | 'fixed' | 'while_paid' | null;
    access_duration_days: number | null;
    stripe_price_id: string | null;
    activated_at: string | null;
}

interface MarginPreview {
    currency: string;
    gross_minor: number;
    stripe_fee_minor_estimate: number;
    aimdoc_fee_minor: number;
    aimdoc_fee_bps: number;
    credits_per_wake_estimate: number | null;
    expected_wakes: number | null;
    credit_cost_minor_estimate: number | null;
    publisher_net_minor_estimate: number;
    margin_bps_estimate: number;
    unbounded_usage: boolean;
    thin_margin: boolean;
    negative_margin: boolean;
    note: string;
}

interface PriceResponse {
    price: ServicePrice | null;
    preview: MarginPreview | null;
    activation_blockers: string[] | null;
    charge_capable: boolean;
    account: { status: string } | null;
}

/** What the form edits — one price per service. Amount in dollars for humans. */
interface Form {
    display_name: string;
    billing_type: 'one_time' | 'subscription';
    amount: string;
    recurring_interval: 'day' | 'week' | 'month' | 'year';
    access_duration_type: 'perpetual' | 'fixed';
    access_duration_days: string;
    expected_wakes: string;
}

const money = (minor: number, currency = 'usd') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(minor / 100);

const fromPrice = (price: ServicePrice | null): Form => ({
    display_name: price?.display_name ?? '',
    billing_type: price?.billing_type ?? 'subscription',
    amount: price ? (price.unit_amount_minor / 100).toFixed(2) : '',
    recurring_interval: price?.recurring_interval ?? 'month',
    access_duration_type: price?.access_duration_type === 'fixed' ? 'fixed' : 'perpetual',
    access_duration_days: price?.access_duration_days ? String(price.access_duration_days) : '30',
    expected_wakes: '',
});

const toPayload = (form: Form) => ({
    display_name: form.display_name.trim() || null,
    billing_type: form.billing_type,
    unit_amount_minor: Math.round(parseFloat(form.amount || '0') * 100),
    currency: 'usd',
    recurring_interval: form.billing_type === 'subscription' ? form.recurring_interval : null,
    recurring_interval_count: form.billing_type === 'subscription' ? 1 : null,
    access_duration_type: form.billing_type === 'one_time' ? form.access_duration_type : null,
    access_duration_days: form.billing_type === 'one_time' && form.access_duration_type === 'fixed' ? Number(form.access_duration_days) : null,
    expected_wakes: form.expected_wakes ? Number(form.expected_wakes) : null,
});

const MarginTable: React.FC<{ preview: MarginPreview; netLabel: string }> = ({ preview, netLabel }) => (
    <dl className="mt-3 grid max-w-md gap-y-0.5 text-sm sm:grid-cols-2">
        <dt className="text-muted-foreground">Buyer pays</dt>
        <dd className="sm:text-right">{money(preview.gross_minor, preview.currency)}</dd>
        <dt className="text-muted-foreground">Stripe processing (est.)</dt>
        <dd className="sm:text-right">− {money(preview.stripe_fee_minor_estimate, preview.currency)}</dd>
        <dt className="text-muted-foreground">Aimdoc platform fee ({preview.aimdoc_fee_bps / 100}%)</dt>
        <dd className="sm:text-right">− {money(preview.aimdoc_fee_minor, preview.currency)}</dd>
        <dt className="text-muted-foreground">Aimdoc credits (est.{preview.expected_wakes ? `, ${preview.expected_wakes} wakes` : ''})</dt>
        <dd className="sm:text-right">
            {preview.credit_cost_minor_estimate == null ? '—' : `− ${money(preview.credit_cost_minor_estimate, preview.currency)}`}
        </dd>
        <dt className="font-medium">{netLabel}</dt>
        <dd className="font-medium sm:text-right">
            {money(preview.publisher_net_minor_estimate, preview.currency)}
            {!preview.unbounded_usage && ` (${(preview.margin_bps_estimate / 100).toFixed(0)}%)`}
        </dd>
    </dl>
);

/**
 * Service → Pricing: the one price a Stripe-sold service is bought as
 * (routes/services_commerce.py, keyed by service id). Draft edits never
 * touch Stripe; Activate creates the Product/Price in the vendor's connected
 * account. An active price is immutable — editing opens a new draft that
 * replaces it on activation. Nothing here bounds usage; the job's own
 * budget does.
 */
const ServicePricing: React.FC<{ serviceId: string }> = ({ serviceId }) => {
    const { get, put, post } = useApi();
    const [data, setData] = useState<PriceResponse | null>(null);
    const [form, setForm] = useState<Form>(fromPrice(null));
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activating, setActivating] = useState(false);
    const [ackThin, setAckThin] = useState(false);
    const apply = useCallback((next: PriceResponse) => {
        setData(next);
        setForm(fromPrice(next.price));
        setEditing(!next.price || next.price.status === 'draft');
    }, []);

    useEffect(() => {
        get(`/services/${serviceId}/price`)
            .then((response) => apply(response.data))
            .catch((err) => toast.error(errorDetail(err, 'Could not load pricing.')))
            .finally(() => setLoading(false));
    }, [serviceId, get, apply]);

    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const saveDraft = async () => {
        setSaving(true);
        try {
            const response = await put(`/services/${serviceId}/price`, toPayload(form));
            apply(response.data);
            setEditing(true);
            toast.success('Draft price saved');
        } catch (err: any) {
            toast.error(errorDetail(err, 'Could not save the price.'));
        } finally {
            setSaving(false);
        }
    };

    const activate = async () => {
        if (!data?.price) return;
        setActivating(true);
        try {
            const response = await post(`/services/${serviceId}/price/activate`, {
                price_id: data.price.id,
                acknowledge_thin_margin: ackThin,
                expected_wakes: form.expected_wakes ? Number(form.expected_wakes) : null,
            });
            apply(response.data);
            toast.success('Price is live');
        } catch (err: any) {
            toast.error(errorDetail(err, 'Could not activate the price.'));
        } finally {
            setActivating(false);
        }
    };

    const archive = async () => {
        try {
            const response = await post(`/services/${serviceId}/price/archive`, {});
            apply(response.data);
            toast.success('Price archived — customers can no longer check out');
        } catch (err: any) {
            toast.error(errorDetail(err, 'Could not archive the price.'));
        }
    };

    const preview = data?.preview ?? null;
    const blockers = useMemo(
        () => (data?.activation_blockers ?? []).filter((b) => !(ackThin && b.includes('thin margin'))),
        [data, ackThin],
    );
    const price = data?.price ?? null;

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading pricing…
            </div>
        );
    }

    if (data && !data.charge_capable && !price) {
        return (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
                Finish setting up Stripe payments before pricing this service.{' '}
                <Link className="underline" to="/integrations?tab=stripe-payments">
                    Integrations → Stripe Payments
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {price && price.status === 'active' && !editing && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{price.label}</span>
                            <Badge>Live</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Live since {price.activated_at ? new Date(price.activated_at).toLocaleDateString() : '—'}. Existing subscribers keep these terms if you change them.
                        </p>
                        {preview && <MarginTable preview={preview} netLabel="Your estimated net per payment" />}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                            Change price
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={archive}>
                            Stop selling
                        </Button>
                    </div>
                </div>
            )}

            {editing && (
                <div className="space-y-4 rounded-md border p-4">
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-1">
                            <Label>Billing</Label>
                            <Select value={form.billing_type} onValueChange={(v) => set('billing_type', v as Form['billing_type'])}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="subscription">Subscription (job runs while paid)</SelectItem>
                                    <SelectItem value="one_time">One-time payment</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="price-amount">Price (USD)</Label>
                            <Input id="price-amount" inputMode="decimal" placeholder="50.00" value={form.amount} onChange={(e) => set('amount', e.target.value)} />
                        </div>
                        {form.billing_type === 'subscription' ? (
                            <div className="space-y-1">
                                <Label>Every</Label>
                                <Select value={form.recurring_interval} onValueChange={(v) => set('recurring_interval', v as Form['recurring_interval'])}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="week">Week</SelectItem>
                                        <SelectItem value="month">Month</SelectItem>
                                        <SelectItem value="year">Year</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                <Label>Job stays on</Label>
                                <Select value={form.access_duration_type} onValueChange={(v) => set('access_duration_type', v as Form['access_duration_type'])}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="perpetual">Until it reaches its goal</SelectItem>
                                        <SelectItem value="fixed">For a fixed number of days</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-1">
                            <Label htmlFor="price-name">Display name (optional)</Label>
                            <Input id="price-name" placeholder="e.g. Warehouse accuracy, monthly" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} />
                        </div>
                        {form.billing_type === 'one_time' && form.access_duration_type === 'fixed' && (
                            <div className="space-y-1">
                                <Label htmlFor="price-days">Days</Label>
                                <Input id="price-days" inputMode="numeric" value={form.access_duration_days} onChange={(e) => set('access_duration_days', e.target.value)} />
                            </div>
                        )}
                        <div className="space-y-1">
                            <Label htmlFor="price-expected">Expected wakes per payment</Label>
                            <Input id="price-expected" inputMode="numeric" placeholder="e.g. 30" value={form.expected_wakes} onChange={(e) => set('expected_wakes', e.target.value)} />
                            <p className="text-xs text-muted-foreground">Only for the margin estimate. The job's budget bounds actual usage.</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button onClick={saveDraft} disabled={saving || !form.amount}>
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            {price?.status === 'draft' ? 'Update draft' : 'Save draft'}
                        </Button>
                        {price?.status === 'active' && (
                            <Button variant="ghost" onClick={() => { setEditing(false); setForm(fromPrice(price)); }}>
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {price?.status === 'draft' && preview && (
                <div className="space-y-3 rounded-md border p-4">
                    <div className="flex items-center justify-between">
                        <span className="font-medium">{price.label}</span>
                        <Badge variant="secondary">Draft</Badge>
                    </div>
                    <MarginTable preview={preview} netLabel="Your estimated net" />
                    <p className="text-xs text-muted-foreground">
                        {preview.note}
                        {preview.unbounded_usage && ' Set expected wakes above to see a credit estimate.'}
                    </p>

                    {preview.thin_margin && !preview.negative_margin && (
                        <label className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
                            <Checkbox checked={ackThin} onCheckedChange={(c) => setAckThin(!!c)} />
                            Estimated margin is under 20%. I understand and want to sell at this price.
                        </label>
                    )}
                    {blockers.length > 0 && (
                        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {blockers.map((b) => (
                                <li key={b}>{b}</li>
                            ))}
                        </ul>
                    )}
                    <Button onClick={activate} disabled={activating || blockers.length > 0}>
                        {activating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Activate price
                    </Button>
                </div>
            )}

            <div>
                <h4 className="text-md font-semibold mb-1">Orders</h4>
                <p className="text-sm text-muted-foreground mb-3">
                    Everyone who bought this service through Stripe: what they paid, the fees, what you net, and the job it created.
                </p>
                <ServiceOrders serviceId={serviceId} />
            </div>
        </div>
    );
};

export default ServicePricing;
